import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ApiLogoType,
  NotificationDeliveryMode,
  TempGuid,
  TenantNotificationConfigurationItemResponse,
  TestAiConnectionRequest,
  UpdateTenantAiConfigurationRequest,
  UpdateTenantExportConfigurationRequest,
  UpdateTenantNotificationConfigurationRequest,
  UpdateTenantRequest,
} from '@/api/schema/data-contracts';
import { useApi } from '@/context/api';
import { useAuth } from '@/context/auth';

import type { MutationProps } from '../types';

// Re-export for consumers (e.g. notification-settings)
export type { NotificationDeliveryMode, UpdateTenantNotificationConfigurationRequest };
export type TenantNotificationConfigurationItem = TenantNotificationConfigurationItemResponse;

export const useTenantQueryActions = () => {
  const { auth } = useAuth();
  const {
    tenantController: {
      v1TenantList,
      v1TenantUpdate,
      v1TenantLogoUpdate,
      v1TenantCssUrlUpdate,
      v1TenantAiStatusList,
      v1TenantAiConfigList,
      v1TenantAiConfigUpdate,
      v1TenantAiConfigTestCreate,
      v1TenantExportConfigList,
      v1TenantExportConfigUpdate,
    },
    tenantNotificationConfigurationController: {
      v1TenantNotificationConfigurationList,
      v1TenantNotificationConfigurationUpdate,
    },
  } = useApi();
  const queryClient = useQueryClient();

  const useGetTenant = () =>
    useQuery({
      enabled: Boolean(auth),
      queryKey: ['tenant'],
      queryFn: async () => {
        const { data } = await v1TenantList();
        return data;
      },
    });

  const useUpdateTenant = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['tenant-update'],
      mutationFn: async (updateTenant: UpdateTenantRequest) => {
        const { data } = await v1TenantUpdate(updateTenant);
        return data;
      },
      onSuccess: async () => await queryClient.invalidateQueries({ queryKey: ['tenant'] }),
    });

  const useUpdateTenantLogo = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['tenant-logo-update'],
      mutationFn: async ({ file, logoType }: { file: File; logoType: ApiLogoType }) => {
        const { data } = await v1TenantLogoUpdate({ logoType }, { file });
        return data;
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['tenant'] });
      },
    });

  const useUpdateTenantCssUrl = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['tenant-css-url-update'],
      mutationFn: async ({ url, style }: { url?: TempGuid; style: string }) => {
        const { data } = await v1TenantCssUrlUpdate(style, { url });
        return data;
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['tenant'] });
      },
    });

  // EWM-631: Lightweight AI status fetch — non-admin (Estimator) users can call this so the
  // Sidebar's AI Advisory menu visibility no longer depends on the admin-gated full config.
  const useGetTenantAiStatus = () =>
    useQuery({
      enabled: Boolean(auth),
      queryKey: ['tenant-ai-status'],
      queryFn: async () => {
        const { data } = await v1TenantAiStatusList();
        return data;
      },
    });

  // EWM-667: `ai-config` is admin-gated (CAN_MANAGE_TENANT_SETTINGS) and returns 403 for
  // Estimators. Callers pass `enabled: false` for non-admins so the request never fires and
  // the global AxiosErrorHandler doesn't surface a spurious "not authorized" toast.
  const useGetTenantAiConfig = (options?: { enabled?: boolean }) =>
    useQuery({
      enabled: Boolean(auth) && (options?.enabled ?? true),
      queryKey: ['tenant-ai-config'],
      queryFn: async () => {
        const { data } = await v1TenantAiConfigList();
        return data;
      },
    });

  const useUpdateTenantAiConfig = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['tenant-ai-config-update'],
      mutationFn: async (updateAiConfig: UpdateTenantAiConfigurationRequest) => {
        const { data } = await v1TenantAiConfigUpdate(updateAiConfig);
        return data;
      },
      // EWM-631: also invalidate the lightweight ai-status query so every consumer (incl. the
      // Sidebar for non-admin users) refreshes its enabled flag immediately after the admin saves.
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['tenant-ai-config'] });
        await queryClient.invalidateQueries({ queryKey: ['tenant-ai-status'] });
      },
    });

  const useTestAiConnection = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['tenant-ai-config-test'],
      mutationFn: async (testConfig: TestAiConnectionRequest) => {
        const { data } = await v1TenantAiConfigTestCreate(testConfig);
        return data;
      },
    });

  // EWM-667: `export-config` is admin-gated (CAN_MANAGE_TENANT_SETTINGS) and returns 403 for
  // Estimators. Callers pass `enabled: false` for non-admins so the request never fires and
  // the global AxiosErrorHandler doesn't surface a spurious "not authorized" toast.
  const useGetTenantExportConfig = (options?: { enabled?: boolean }) =>
    useQuery({
      enabled: Boolean(auth) && (options?.enabled ?? true),
      queryKey: ['tenant-export-config'],
      queryFn: async () => {
        const { data } = await v1TenantExportConfigList();
        return data;
      },
    });

  const useUpdateTenantExportConfig = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['tenant-export-config-update'],
      mutationFn: async (updateConfig: UpdateTenantExportConfigurationRequest) => {
        const { data } = await v1TenantExportConfigUpdate(updateConfig);
        return data;
      },
      onSuccess: async () =>
        await queryClient.invalidateQueries({ queryKey: ['tenant-export-config'] }),
    });

  const useGetTenantNotificationConfig = (options?: { enabled?: boolean }) =>
    useQuery({
      enabled: Boolean(auth) && (options?.enabled ?? true),
      queryKey: ['tenant-notification-config'],
      queryFn: async () => {
        const { data } = await v1TenantNotificationConfigurationList();
        return data;
      },
    });

  const useUpdateTenantNotificationConfig = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['tenant-notification-config-update'],
      mutationFn: async (updateConfig: UpdateTenantNotificationConfigurationRequest) => {
        await v1TenantNotificationConfigurationUpdate(updateConfig);
      },
      onSuccess: async () =>
        await queryClient.invalidateQueries({ queryKey: ['tenant-notification-config'] }),
    });

  return {
    useGetTenant,
    useUpdateTenant,
    useUpdateTenantLogo,
    useUpdateTenantCssUrl,
    useGetTenantAiStatus,
    useGetTenantAiConfig,
    useUpdateTenantAiConfig,
    useTestAiConnection,
    useGetTenantExportConfig,
    useUpdateTenantExportConfig,
    useGetTenantNotificationConfig,
    useUpdateTenantNotificationConfig,
  };
};
